/**
 * Rules live in the query string, like the weights do, so a recommendation
 * narrowed by rules is bookmarkable and the back button behaves.
 *
 * A rule is a single flat string:
 *
 *   `field:comparison`                 a rule drawn across the two meals
 *   `field:comparison:scope:value`     a rule judged one meal at a time
 *
 * The server owns this grammar and validates it; this module is the client half
 * -- parsing, formatting and turning a rule into a sentence for the list. What
 * each field and comparison means comes from the catalogue the API serves, so
 * the editor and the validator cannot disagree.
 */

import type { RuleCatalog, RuleComparison, RuleField } from '@/api/types'

export const RULE_PARAM = 'rule'

export interface Rule {
  field: string
  comparison: string
  /** Absent for a rule that spans the two meals. */
  scope?: string
  /** Absent when the comparison takes no value. */
  value?: string
}

export function parseRule(text: string): Rule | null {
  const parts = text.split(':')
  if (parts.length !== 2 && parts.length !== 4) return null
  const [field, comparison, scope, value] = parts
  if (!field || !comparison) return null
  return parts.length === 2
    ? { field, comparison }
    : { field, comparison, scope, value }
}

export function formatRule(rule: Rule): string {
  return rule.scope === undefined
    ? `${rule.field}:${rule.comparison}`
    : `${rule.field}:${rule.comparison}:${rule.scope}:${rule.value ?? ''}`
}

export function parseRules(params: URLSearchParams, param = RULE_PARAM): string[] {
  return params.getAll(param).filter((value) => value !== '')
}

/**
 * Whether the URL says anything about the rules.
 *
 * Three things can be said, and they are not the same: nothing at all, which
 * leaves the server's defaults in charge; an empty rule, which asks for no
 * rules; and a list of rules.
 */
export function hasRuleParam(params: URLSearchParams, param = RULE_PARAM): boolean {
  return params.getAll(param).length > 0
}

/**
 * The rules the catalogue describes, and nothing else.
 *
 * A rule the catalogue has never heard of is dropped rather than sent: the
 * server would reject it, and a mistyped rule should narrow the search rather
 * than break the request.
 */
export function knownRules(rules: string[], catalog: RuleCatalog | undefined): Rule[] {
  const parsed = rules.map(parseRule).filter((rule): rule is Rule => rule !== null)
  if (!catalog) return parsed
  return parsed.filter((rule) => describeRule(rule, catalog) !== null)
}

/**
 * The rules in force, as the URL asks for them.
 *
 * A URL that says nothing about the rules leaves the server's defaults in
 * charge, so those are what comes back: the rules on screen are the rules
 * being applied.
 */
export function readRules(
  params: URLSearchParams,
  catalog: RuleCatalog | undefined,
  param = RULE_PARAM,
): Rule[] {
  const parsed = knownRules(parseRules(params, param), catalog)
  if (parsed.length > 0 || hasRuleParam(params, param)) return parsed
  return knownRules(catalog?.defaults ?? [], catalog)
}

/**
 * The rule parameters to send with a request.
 *
 * A URL that says nothing sends no parameter at all, which is how the server's
 * defaults are asked for. A URL that says "no rules" sends an empty one, the
 * only way to ask for none.
 */
export function ruleParams(
  params: URLSearchParams,
  catalog: RuleCatalog | undefined,
  param = RULE_PARAM,
): string[] {
  const carried = params.getAll(param)
  if (carried.length === 0) return []
  const rules = readRules(params, catalog, param)
  const ready = rules.filter((rule) => isComplete(rule, catalog))
  if (ready.length > 0) return writeRules(ready)
  // Every rule the URL carried was unreadable or unfinished. An empty parameter
  // is how "no rules" is said; anything else is a typo, and a typo should not
  // widen the search.
  if (rules.length > 0) return ['']
  return carried.every((value) => value === '') ? [''] : []
}

export function writeRules(rules: Rule[]): string[] {
  return rules.map(formatRule)
}

// ----- reading a rule out loud -----


function fieldSpec(
  rule: Rule,
  catalog: RuleCatalog | undefined,
): RuleField | undefined {
  return catalog?.fields.find((field) => field.key === rule.field)
}

function comparisonSpec(
  rule: Rule,
  catalog: RuleCatalog | undefined,
): RuleComparison | undefined {
  return fieldSpec(rule, catalog)?.comparisons.find(
    (comparison) => comparison.key === rule.comparison,
  )
}

/**
 * The sentence a rule reads as, or null if the catalogue has never heard of it.
 * Shown in the list so the user reads "At least one meal is vegetarian" rather
 * than four dropdowns.
 */
export function describeRule(rule: Rule, catalog: RuleCatalog): string | null {
  const comparison = comparisonSpec(rule, catalog)
  if (!comparison) return null

  const label =
    rule.scope === 'combined'
      ? comparison.label.replace('provides', 'provide')
      : comparison.label
  const rendered = renderValue(comparison, rule.value)

  if (comparison.scopes.length === 0) {
    return `The two meals ${label}`
  }
  if (rule.scope === undefined) return null
  const prefix = catalog.scope_labels[rule.scope]
  if (!prefix) return null
  return `${prefix} ${label} ${rendered}`.trim()
}

function renderValue(comparison: RuleComparison, value: string | undefined): string {
  const kind = comparison.value_kind
  if (kind === 'boolean') return value === 'true' ? 'vegetarian' : 'not vegetarian'
  if (kind === 'days') {
    const days = Number(value)
    return `${days} day${days === 1 ? '' : 's'} ago`
  }
  if (kind === 'times') {
    const times = Number(value)
    return `${times} time${times === 1 ? '' : 's'}`
  }
  if (kind === 'decimal') {
    return `${value ?? ''} ${comparison.unit ?? ''}`.trim()
  }
  // Several values are held joined by a pipe, and read as a list.
  const items = (value ?? '').split('|').filter(Boolean)
  return items.map((item) => `"${item}"`).join(', ')
}

// ----- building rules from the catalogue -----

/**
 * A sensible starting point for a newly added rule: the field's first
 * comparison, its first scope, and a value that satisfies the declared bounds.
 */
export function newRule(catalog: RuleCatalog): Rule {
  const field = catalog.fields[0]
  const comparison = field?.comparisons[0]
  if (!field || !comparison) return { field: '', comparison: '' }
  if (comparison.scopes.length === 0) {
    return { field: field.key, comparison: comparison.key }
  }
  return {
    field: field.key,
    comparison: comparison.key,
    scope: comparison.scopes[0],
    value: defaultValue(comparison),
  }
}

export function defaultValue(comparison: RuleComparison, options: string[] = []): string {
  switch (comparison.value_kind) {
    case 'boolean':
      return 'true'
    case 'days':
      return '60'
    case 'times':
      return '1'
    case 'number':
    case 'decimal':
      return String(comparison.min ?? 0)
    default:
      // A value drawn from the collection: the first one on offer, so the rule
      // says something the moment it is made.
      return options[0] ?? ''
  }
}

/**
 * Whether a rule says enough to be applied.
 *
 * A rule that needs a value and has none is half-finished: the server would
 * reject it, so it is left out of the request until it is finished.
 */
export function isComplete(rule: Rule, catalog: RuleCatalog | undefined): boolean {
  const comparison = comparisonSpec(rule, catalog)
  if (!comparison) return false
  if (comparison.scopes.length === 0) return true
  if (rule.scope === undefined) return false
  if (comparison.value_kind === 'none') return true
  return (rule.value ?? '') !== ''
}